import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Loader2, UserCheck } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { FollowButton } from '@/components/ui/FollowButton'
import { socialApi, usersApi, User } from '@/lib/api'
import { getInitials } from '@/lib/utils'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'

// Component to fetch and display a single following user
function FollowingItem({
  followingId,
  index,
  currentUser,
  onUserClick,
}: {
  followingId: string
  index: number
  currentUser: User | null
  onUserClick: (userId: string, username: string) => void
}) {
  const { data: userData, isLoading } = useQuery({
    queryKey: ['user', followingId],
    queryFn: () => usersApi.getProfile(followingId),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 rounded-lg p-3">
        <div className="h-12 w-12 animate-pulse rounded-full bg-muted" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-32 animate-pulse rounded bg-muted" />
          <div className="h-3 w-24 animate-pulse rounded bg-muted" />
        </div>
      </div>
    )
  }

  const apiData = userData?.data as any
  const user = apiData?.user
  if (!user) return null

  const displayName = apiData.profile?.display_name || user.display_name || user.username
  const avatarUrl = apiData.profile?.avatar_url
  const bio = apiData.profile?.bio

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ delay: index * 0.03 }}
      className="flex items-center justify-between rounded-lg p-3 transition-colors hover:bg-muted"
    >
      <button
        onClick={() => onUserClick(user.id, user.username)}
        className="flex flex-1 items-center gap-3 text-left"
      >
        <Avatar className="h-12 w-12">
          <AvatarImage src={avatarUrl} />
          <AvatarFallback>{getInitials(displayName)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{displayName}</p>
          <p className="text-sm text-muted-foreground truncate">
            @{user.username}
          </p>
          {bio && (
            <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
              {bio}
            </p>
          )}
        </div>
      </button>

      {currentUser?.id !== user.id && (
        <FollowButton
          userId={user.id}
          username={user.username}
          isFollowing={apiData.is_following ?? true}
          size="sm"
          variant="outline"
        />
      )}
    </motion.div>
  )
}

interface FollowingModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  userId: string
  username: string
  initialCount?: number
}

export function FollowingModal({
  open,
  onOpenChange,
  userId,
  username,
  initialCount = 0,
}: FollowingModalProps) {
  const navigate = useNavigate()
  const { user: currentUser } = useAuth()
  const [page, setPage] = useState(1)
  const pageSize = 20

  const { data: followingData, isLoading } = useQuery({
    queryKey: ['following', userId, page],
    queryFn: async () => {
      const response = await socialApi.getFollowing(userId, page, pageSize)
      // Response contains Follow objects with following_id
      const follows = response.data?.data || []

      if (follows.length === 0) return { data: [], total: 0 }

      return {
        data: follows,
        total: response.data?.pagination?.total || 0
      }
    },
    enabled: open,
  })

  const following = followingData?.data || []
  const total = followingData?.total || initialCount

  const handleUserClick = (clickedUserId: string, clickedUsername: string) => {
    onOpenChange(false)
    navigate(`/profile/${clickedUsername}`)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            Following
            {total > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({total})
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : following.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <UserCheck className="mx-auto h-12 w-12 opacity-20" />
              <p className="mt-4">Not following anyone yet</p>
            </div>
          ) : (
            <div className="space-y-1">
              <AnimatePresence>
                {following.map((follow: any, index: number) => {
                  // Follow object has follower_id and following_id
                  // For following list, we want to show the following user
                  const followingId = follow.following_id || follow.followingID

                  return (
                    <FollowingItem
                      key={follow.id || followingId}
                      followingId={followingId}
                      index={index}
                      currentUser={currentUser}
                      onUserClick={handleUserClick}
                    />
                  )
                })}
              </AnimatePresence>
            </div>
          )}

          {/* Pagination */}
          {!isLoading && following.length > 0 && (
            <div className="mt-4 flex items-center justify-between border-t pt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {Math.ceil(total / pageSize)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= Math.ceil(total / pageSize)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
